import { useEffect, useMemo } from "react";
import { api } from "../../lib/api";
import { PersonCard, ServiceCard, CompanyCard, JobCard, ProjectCard, RfqCard, ItemCard } from "../../components/cards";
import { rfqDeadlineExpired } from "../../lib/rfqDeadline";
import { IMAGE_TYPES, MULTI_TYPES, JOB_WORK_ARRANGEMENTS, DEFAULT_ITEM_SALE_UNIT, PICKUP_LOCATION_REQUIRED_MESSAGE, requirementValues, publishedRequirements } from "./model";
import type { PostFlowDraftModel } from "./usePostFlowDraft";

export function usePostFlowPreview(model: PostFlowDraftModel) {
  const {
    type, map, user, step, setStep, form, itemPickupHours, pickupHoursError, itemPickupLocation,
    rentalRatePeriod, itemRules, canPostRent, itemCatalogLoading, itemCatalogError,
    selectedItemType, requiresItemCondition, supportsProductInformation, productInformationError,
    isBusinessListing, supportsItemHandover, itemHandoverOptions, hasDefaultPickup,
    requiresItemLocation, setError, setJobDetailsAttempted, projectRequirements, projectLicenses,
    selectedProjectLicenses, projectDateInputValid, deadlineInputValid, images, extraTitles,
    rfqItems, rfqDesiredMilestones, isServicesRfq, setResumeAttachments, selectedResumeId,
    setSelectedResumeId, setNewResumeIds, resumeFileBusy, setResumeFileBusy, set,
    invalidJobField, jobDetailsError, cleanItems, rfqDetailsError, rfqDesiredTermsError,
    cleanList, draft
  } = model;
  async function uploadResumeAttachment(file?: File | null) {
    if (!file) return;
    setResumeFileBusy(true);
    setError(null);
    try {
      const r = await api.uploadResume(file);
      const id = r?.attachment?.id;
      if (id) {
        setNewResumeIds((ids) => ids.includes(id) ? ids : [...ids, id]);
        await api.setActiveResume(id);
        setSelectedResumeId(id);
      }
      const latest = await api.myResumes();
      setResumeAttachments(latest?.attachments || []);
    } catch (e: any) {
      setError(e?.message || "Could not upload the résumé file.");
    } finally {
      setResumeFileBusy(false);
    }
  }
  async function chooseResumeAttachment(id: string) {
    setResumeFileBusy(true);
    setError(null);
    try {
      await api.setActiveResume(id);
      setSelectedResumeId(id);
      setResumeAttachments((rows) => rows?.map((a) => ({ ...a, active: a.id === id })) || rows);
    } catch (e: any) {
      setError(e?.message || "Could not select this résumé file.");
    } finally {
      setResumeFileBusy(false);
    }
  }
  function buildPayload() {
    const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
    const amt = Number(String(form.amount).replace(/[^\d.]/g, "")) || 0;
    const cat = form.category.trim() || "General";
    switch (type) {
      case "job":
        // Existing job readers derive arrangement from tags; put it first so the server's tag limit cannot discard it.
        return { title: form.title, category: cat, city: form.location, salary: amt ? `PHP ${amt.toLocaleString()}/mo` : undefined, type: "Full-time", description: form.description.trim() || undefined, benefits: form.benefits.trim() || undefined, tags: [...(form.workArrangement ? [form.workArrangement] : []), ...tags.filter((tag) => !JOB_WORK_ARRANGEMENTS.some((arrangement) => arrangement.toLowerCase() === tag.toLowerCase()))] };
      case "service":
      case "company-service":
        return { title: form.title, category: cat, city: form.location, fromPrice: amt, company: type === "company-service", description: form.description.trim() || undefined, previousWork: form.previousWork || undefined, whatsIncluded: form.whatsIncluded.trim() || undefined, requirements: form.requirements.trim() || undefined, tags };
      case "project":
        return { title: form.title, category: cat, location: form.location.trim(), publicLocation: form.location.trim(), budget: amt, budgetType: "Estimated", deadline: form.deadline, targetCompletionDate: form.targetCompletionDate || undefined, description: form.description, requirements: cleanList(publishedRequirements(projectRequirements)), licenses: cleanList([...selectedProjectLicenses, ...requirementValues(projectLicenses)]) };
      case "rfq":
        // [rfq.request-type](../../../../docs/product/rfqs.md#rfq-request-type): retain the Items draft for switching, but exclude it from Services publication.
        return { title: form.title, requestType: form.requestType, category: cat, location: form.location, deadline: form.deadline, description: isServicesRfq ? form.description.trim() : form.description, previousWork: form.previousWork || undefined, items: isServicesRfq ? [] : cleanItems(), desiredMilestones: isServicesRfq ? rfqDesiredMilestones.map(stage => ({ label: stage.label.trim(), percentage: Number(stage.percentage) })) : undefined };
      case "resume": {
        const rateNum = Number(String(form.rateAmount).replace(/[^\d.]/g, "")) || 0;
        const priceStr = rateNum ? `₱${rateNum.toLocaleString()}/${form.ratePeriod}` : "";
        const termStr = form.term && form.term !== "Negotiable" ? form.term : "";
        const rate = [priceStr, termStr].filter(Boolean).join(" · ") || undefined;
        return { title: form.title, city: form.location, years: amt, rate, skills: tags, about: form.description || undefined, resumeAttachmentId: selectedResumeId || undefined };
      }
      case "company":
        return { name: form.title, tagline: form.description, city: form.location, tags };
      case "item":
        // [items.pickup-defaults](../../../../docs/product/items.md#items-pickup-defaults): location stays public independently of handover; Pickup requires it.
        return {
          title: form.title, category: cat, location: form.location,
          kind: form.kind,
          mode: form.kind === "business" ? "sale" : form.mode,
          price: amt,
          unit: form.kind === "business" ? "business" : form.mode === "rent" ? "day" : form.kind === "property" ? "property" : (form.unit || DEFAULT_ITEM_SALE_UNIT),
          stock: selectedItemType?.quantityApplicable ? (Number(String(form.stock).replace(/[^\d]/g, "")) || 0) : 1,
          itemType: form.itemType,
          condition: requiresItemCondition ? form.condition || null : null,
          businessOperatingStatus: isBusinessListing ? form.businessOperatingStatus || null : null,
          handoverOptions: supportsItemHandover ? itemHandoverOptions : [],
          // [items.pickup-defaults](../../../../docs/product/items.md#items-pickup-defaults): blank hours retain negotiation; the API requires pickup defaults as a complete pair.
          pickupLocation: hasDefaultPickup ? itemPickupLocation : null,
          pickupHours: hasDefaultPickup ? itemPickupHours : null,
          description: form.description, productInformation: supportsProductInformation ? form.productInformation : null, tags,
        };
      default:
        return {};
    }
  }
  function validateStep(s: number): string | null {
    if (s === 4 && type === "rfq") {
      const termsError = rfqDesiredTermsError();
      if (termsError) return termsError;
    }
    if (type === "item" && form.mode === "rent" && !canPostRent) return "Marketplace rentals are temporarily unavailable. Choose For Sale.";
    if (s === 4 && supportsItemHandover && itemHandoverOptions.includes("pickup") && pickupHoursError) return pickupHoursError;
    if (s === 4 && requiresItemLocation && !itemPickupLocation) return PICKUP_LOCATION_REQUIRED_MESSAGE;
    if (s === 4 && ["job", "project", "rfq"].includes(type) && !deadlineInputValid) return "Enter a valid deadline (MM/DD/YYYY), or clear the field.";
    if (s === 4 && type === "rfq" && rfqDeadlineExpired(form.deadline)) return "Choose today or a future quote deadline.";
    if (s === 4 && type === "item" && (!/^\d+(?:\.\d{1,2})?$/.test(form.amount) || Number(form.amount) <= 0)) return "Enter a valid price greater than zero.";
    if (s === 2) {
      // [jobs.listing-validation](../../../../docs/product/jobs.md#jobs-listing-validation): preflight uses the submitted strings, including the
      // complete picker address, so Details and Review cannot send invalid text.
      if (jobDetailsError) return jobDetailsError;
      if (type === "item" && itemCatalogLoading) return "Wait for the item types to load.";
      if (type === "item" && itemCatalogError) return "Retry loading the item types before continuing.";
      if (type === "project" && !projectDateInputValid) return "Enter a valid target completion date (MM/DD/YYYY), or clear the field.";
      if (type === "item" && !form.mode) return "Choose whether the item is For Sale or For Rent.";
      if (!form.title.trim() && (!MULTI_TYPES.includes(type) || extraTitles.length === 0)) return type === "company" ? "A company name is required to continue." : "A title is required to continue.";
      if (type === "job" && !form.workArrangement) return "Choose whether this job is on-site, hybrid, or remote.";
      if (type === "rfq") {
        const detailsError = rfqDetailsError();
        if (detailsError) return detailsError;
      }
      if (type === "item" && (!selectedItemType || !selectedItemType.modes.some((mode) => mode === form.mode))) return "Select the item type to continue.";
      if (type === "item" && requiresItemCondition && !form.condition) return "Select the item condition to continue.";
      if (type === "item" && selectedItemType?.conditionProfile === "business" && !form.businessOperatingStatus) return "Select the business operating status to continue.";
      if (productInformationError) return productInformationError;
    }
    if (s === 3 && IMAGE_TYPES.includes(type) && images.length < 1) {
      return "Add at least 1 photo to continue.";
    }
    return null;
  }
  const reviewError = validateStep(2) || validateStep(3) || validateStep(4);
  // The media step includes photo galleries, optional RFQ photos, and résumé files.
  const hasMediaStep = IMAGE_TYPES.includes(type) || type === "rfq" || type === "resume";
  // [17]/[65] Live preview reflects the first uploaded photo too (not just text).
  const coverPreview = useMemo(() => (images[0] ? URL.createObjectURL(images[0]) : null), [images]);
useEffect(() => () => { if (coverPreview) URL.revokeObjectURL(coverPreview); }, [coverPreview]);
  // [#26] Live preview uses marketplace card components with available draft labels.
  // We shape the form into the object each card expects, filling neutral defaults for values that
  // only exist post-publish (id, ratings, bid/quote/applicant counts).
  const previewModel = useMemo(() => {
    const base: any = buildPayload();
    const cover = coverPreview || undefined;
    const author = user ? `${user.firstName} ${user.lastName}`.trim() : "You";
    const initials = user ? `${(user.firstName || "?")[0] || "?"}${(user.lastName || "")[0] || ""}`.toUpperCase() : "You";
    switch (type) {
      case "job":
        return { ...base, id: "preview", company: author, applicants: 0, posted: "just now", coverImage: cover, tags: base.tags || [] };
      case "service":
      case "company-service":
        return { ...base, id: "preview", by: author, from: base.fromPrice || 0, rating: 0, reviews: 0, verified: false, coverImage: cover, tags: base.tags || [] };
      case "project":
        return { ...base, id: "preview", title: base.title || "Your project title", location: base.location || "City not set", bids: 0, budget: base.budget || 0, coverImage: cover, deadline: base.deadline || "—" };
      case "rfq":
        return { ...base, detail: { requestType: form.requestType, desiredMilestones: base.desiredMilestones }, id: "preview", coverImage: cover, quotes: 0, items: base.items || [], deadline: base.deadline || "—" };
      case "resume":
        return { id: "preview", name: author, initials, title: base.title || "Your headline", city: base.city || "—", years: base.years || 0, rate: base.rate || "", skills: base.skills || [], rating: 0, reviews: 0, available: true, verified: false };
      case "company":
        return { ...base, id: "preview", initials, rating: 0, reviews: 0, verified: false, since: String(new Date().getFullYear()), tags: base.tags || [] };
      case "item":
        return { ...base, unit: form.mode === "rent" ? rentalRatePeriod : base.unit, id: "preview", coverImage: cover, sellerRating: { average: 0, count: 0 }, tags: base.tags || [] };
      default:
        return { ...base, id: "preview" };
    }
  }, [form, images, rfqItems, rfqDesiredMilestones, type, user, coverPreview, itemRules, itemHandoverOptions, rentalRatePeriod]);
  function renderPreviewCard() {
    const m = previewModel as any;
    switch (type) {
      case "job": return <JobCard j={m} />;
      case "service": case "company-service": return <ServiceCard s={m} />;
      case "project": return <ProjectCard p={m} />;
      case "rfq": return <RfqCard r={m} />;
      case "resume": return <PersonCard p={m} />;
      case "company": return <CompanyCard c={m} />;
      case "item": return <ItemCard it={m} typeLabel={selectedItemType?.label} />;
      default: return null;
    }
  }
  function goNext() {
    if (type === "resume" && resumeFileBusy) {
      setError("Wait for the résumé upload to finish before continuing.");
      return;
    }
    const msg = validateStep(step);
    if (type === "job" && step === 2) setJobDetailsAttempted(true);
    if (msg) {
      setError(msg);
      if (step === 2 && invalidJobField) document.getElementById(invalidJobField === "location" ? "posting-job-location" : invalidJobField === "benefits" ? "job-benefits" : `posting-${invalidJobField}`)?.focus();
      return;
    }
    setError(null);
    setStep(step === 2 && !hasMediaStep ? 4 : step + 1);
  }
  function goPrev() {
    setError(null);
    setStep(step === 4 && !hasMediaStep ? 2 : Math.max(1, step - 1));
  }
  // Each wizard step is a new viewport; do not carry the previous step's scroll
  // position into it when the form content changes height.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* ignore */ }
  }, [step]);
  return {
    ...model, uploadResumeAttachment, chooseResumeAttachment, buildPayload, validateStep,
    reviewError, hasMediaStep, coverPreview, previewModel, renderPreviewCard, goNext, goPrev
  };
}
export type PostFlowPreviewModel = ReturnType<typeof usePostFlowPreview>;
